import { Fragment, useState } from 'react';
import clsx from 'clsx';
import { caretRight } from '../../../../assets/hostedassets';
import { ALL_KNOWN_DETAILS_TESTIDS, META_SEPARATOR_DOT } from '../../constants';
import { countLabel, flattenValues, groupsForType } from '../../utils/allKnownDetailsUtils';
import type { KnownDetailType, KnownEntity } from '../../types';
import SourceGroupEntries from '../SourceGroupEntries/SourceGroupEntries';
import styles from './EntityAccordion.module.scss';

interface EntityAccordionProps {
    entity: KnownEntity;
    type: KnownDetailType;
}

/** DOM-safe id: an entityId may hold characters invalid in an id. */
const bodyIdFor = (entityId: string, type: KnownDetailType) =>
    `${ALL_KNOWN_DETAILS_TESTIDS.ENTITY}-${entityId.replace(/[^a-zA-Z0-9]/g, '')}-${type}`;

const EntityAccordion = ({ entity, type }: EntityAccordionProps) => {
    const [expanded, setExpanded] = useState(true);
    const bodyId = bodyIdFor(entity.entityId, type);
    const groups = groupsForType(entity, type);
    const meta = [entity.designation, entity.shareholdingPercent === undefined ? undefined : `${entity.shareholdingPercent}%`].filter(
        (entry): entry is string => entry !== undefined,
    );

    return (
        <div className={styles.Entity} data-testid={ALL_KNOWN_DETAILS_TESTIDS.ENTITY}>
            <button
                type="button"
                className={styles.EntityHeader}
                aria-expanded={expanded}
                // aria-controls only while the body is mounted.
                aria-controls={expanded ? bodyId : undefined}
                onClick={() => setExpanded((wasExpanded) => !wasExpanded)}
            >
                <span className={styles.EntityHeading}>
                    <span className={styles.EntityName}>{entity.name}</span>
                    {meta.map((entry) => (
                        <Fragment key={entry}>
                            <span className={styles.Dot} aria-hidden="true">
                                {META_SEPARATOR_DOT}
                            </span>
                            <span className={styles.EntityMeta}>{entry}</span>
                        </Fragment>
                    ))}
                    <span className={styles.EntityPill} data-testid={ALL_KNOWN_DETAILS_TESTIDS.ENTITY_PILL}>
                        {countLabel(flattenValues(groups).length, type)}
                    </span>
                </span>
                <img src={caretRight} alt="" aria-hidden="true" className={clsx(styles.Chevron, { [styles.ChevronExpanded]: expanded })} />
            </button>
            {expanded && (
                <div id={bodyId} className={styles.EntityBody}>
                    <SourceGroupEntries groups={groups} type={type} entityType={entity.entityType} />
                </div>
            )}
        </div>
    );
};

export default EntityAccordion;
